import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { ChatMessage } from '../api.js';

/**
 * Table talk, above the log and hidden the same way.
 *
 * A line goes to everyone or to one player, picked from the table. A
 * spectator reads what is said to everyone and has nothing to send with.
 */
export function ChatPanel({
  messages, robotIds, youAre, nameOf, onSend,
}: {
  messages: ChatMessage[];
  /** Every seated robot, the viewer's own included. */
  robotIds: string[];
  youAre: string | null;
  nameOf: (robotId: string) => string;
  onSend: (to: string | null, text: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(true);
  /** null is everyone. */
  const [to, setTo] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const others = robotIds.filter((id) => id !== youAre);
  // A recipient who has left the table is not somebody to send to.
  useEffect(() => {
    if (to !== null && !others.includes(to)) setTo(null);
  }, [others, to]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const said = text.trim();
    if (!said || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(to, said);
      // Only what was sent: anything typed since stays.
      setText((current) => (current.trim() === said ? '' : current));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="stack" style={{ gap: '0.4rem' }}>
      <button
        className="secondary log-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? 'Hide the chat' : 'Show the chat'}
      </button>

      {open && (
        <>
          <div className="log chat" role="log" ref={scroller}>
            {messages.length === 0 && <p className="muted">Nothing has been said yet.</p>}
            {messages.map((m) => (
              <p key={m.id} className={m.to ? 'chat-line chat-private' : 'chat-line'}>
                <span className="chat-from">
                  {m.fromName}
                  {m.to && <> to {m.to === youAre ? 'you' : nameOf(m.to)}</>}
                </span>
                {' '}{m.text}
              </p>
            ))}
          </div>

          {youAre && (
            <form className="chat-form" onSubmit={(e) => void send(e)}>
              <select
                className="mono log-filter"
                value={to ?? ''}
                onChange={(e) => setTo(e.target.value || null)}
                aria-label="Send to"
              >
                <option value="">Everyone</option>
                {others.map((robotId) => (
                  <option key={robotId} value={robotId}>{nameOf(robotId)}</option>
                ))}
              </select>
              <input
                className="chat-input"
                value={text}
                maxLength={500}
                onChange={(e) => setText(e.target.value)}
                aria-label="Message"
              />
              <button className="secondary tiny" type="submit" disabled={sending || !text.trim()}>
                Send
              </button>
            </form>
          )}
          {error && <p className="muted">{error}</p>}
        </>
      )}
    </div>
  );
}
